import { useState } from 'react';
import { getValidAdminToken } from '../utils/authHelpers';
import { getApiBaseUrlWithoutPath } from '../../config/environment';
import { getMissingFieldsForCreate, formatMissingFieldsMessage } from '../utils/productFormValidation';
import {
  appendListPriceFormFields,
  getCreateMultipartPricing,
  parsePriceInput,
} from '../utils/productPricing';
import { getAdminUser } from '../utils/authUtils';
import {
  canSetProductFeatured,
  omitProductFeaturedForNonAdmin,
} from '../constants/teamAccessPermissions';

const useCreateProduct = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const MAX_VIDEO_BYTES = 5 * 1024 * 1024;

  const createProduct = async (productData, imageFiles, videoFile = null) => {
    try {
      setLoading(true);
      setError(null);

      const token = await getValidAdminToken();
      const apiUrl = getApiBaseUrlWithoutPath();

      const missing = getMissingFieldsForCreate(productData, imageFiles);
      if (missing.length > 0) {
        throw new Error(formatMissingFieldsMessage(missing));
      }

      const multipartPricing = getCreateMultipartPricing(productData);

      const formData = new FormData();

      formData.append('name', productData.name);
      formData.append('description', productData.description);
      formData.append('category', productData.category || '');
      formData.append('brand', productData.brand || '');
      formData.append('gender', productData.gender || 'Unisex');

      formData.append('price', String(multipartPricing.price));
      appendListPriceFormFields(formData, multipartPricing.originalPrice);

      if (multipartPricing.salePrice != null) {
        formData.append('salePrice', String(multipartPricing.salePrice));
      }
      formData.append('isOnSale', multipartPricing.isOnSale ? 'true' : 'false');

      if (productData.discount !== undefined && productData.discount !== null && productData.discount !== '') {
        formData.append('discount', String(productData.discount));
      }
      if (productData.saleEndDate) {
        formData.append('saleEndDate', String(productData.saleEndDate));
      }

      if (
        productData.descriptionRich != null &&
        Array.isArray(productData.descriptionRich) &&
        productData.descriptionRich.length > 0
      ) {
        formData.append('descriptionRich', JSON.stringify(productData.descriptionRich));
      }

      if (productData.subcategory) {
        formData.append('subcategory', productData.subcategory);
      }
      if (productData.childrenCategory) {
        formData.append('childrenCategory', productData.childrenCategory);
      }
      if (productData.mainCategory) {
        formData.append('mainCategory', productData.mainCategory);
      }
      if (productData.sku) {
        formData.append('sku', productData.sku);
      }
      if (productData.url) {
        formData.append('url', productData.url);
      }
      if (productData.productId) {
        formData.append('productId', productData.productId);
      }
      if (productData.createdBy) {
        formData.append('createdBy', productData.createdBy);
      }

      const adminUser = getAdminUser();
      const safeProductData = omitProductFeaturedForNonAdmin(adminUser, productData);
      if (canSetProductFeatured(adminUser)) {
        formData.append('isFeatured', safeProductData.isFeatured ? 'true' : 'false');
      }
      formData.append('isNew', productData.isNew ? 'true' : 'false');
      if (productData.isActive !== undefined) {
        formData.append('isActive', productData.isActive ? 'true' : 'false');
      }
      if (productData.status) {
        formData.append('status', String(productData.status));
      }

      if (productData.colors && Array.isArray(productData.colors) && productData.colors.length > 0) {
        productData.colors.forEach((color) => {
          formData.append('colors[]', color);
        });
      }
      if (productData.sizes && Array.isArray(productData.sizes) && productData.sizes.length > 0) {
        productData.sizes.forEach((size) => {
          formData.append('sizes[]', size);
        });
      }
      if (productData.stock && typeof productData.stock === 'object' && Object.keys(productData.stock).length > 0) {
        formData.append('stock', JSON.stringify(productData.stock));
      }
      if (productData.tags && Array.isArray(productData.tags) && productData.tags.length > 0) {
        productData.tags.forEach((tag) => {
          const tagValue = typeof tag === 'string' ? tag.trim() : String(tag || '').trim();
          if (tagValue) {
            formData.append('tags[]', tagValue);
          }
        });
      }

      imageFiles.forEach((image) => {
        formData.append('images', image);
      });

      if (
        productData.imagesMeta &&
        Array.isArray(productData.imagesMeta) &&
        productData.imagesMeta.length > 0
      ) {
        formData.append('imagesMeta', JSON.stringify(productData.imagesMeta));
      }

      if (videoFile) {
        if (videoFile.size > MAX_VIDEO_BYTES) {
          throw new Error('Video must be 5MB or smaller.');
        }
        formData.append('productVideo', videoFile);
      }

      const uploadUrl = `${apiUrl}/api/admin/products/upload`;
      const doUpload = (authToken) =>
        fetch(uploadUrl, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        });

      let response = await doUpload(token);

      if (response.status === 401 || response.status === 403) {
        const newToken = await getValidAdminToken();
        response = await doUpload(newToken);
      }

      const responseText = await response.text();
      let result = {};
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch {
        result = { message: responseText || `HTTP ${response.status}` };
      }

      if (!response.ok) {
        const detail =
          result.message ||
          result.error ||
          (Array.isArray(result.errors)
            ? result.errors.map((e) => e.msg || e.message || String(e)).join(', ')
            : null) ||
          responseText ||
          `HTTP ${response.status}`;
        throw new Error(detail);
      }

      if (result.success === false) {
        throw new Error(result.message || result.error || 'Failed to create product');
      }

      const product = result.data?.product || result.data || result;
      return {
        ...product,
        _multipartPricing: multipartPricing,
        _formPricing: {
          originalPrice: parsePriceInput(productData.originalPrice),
          salePrice: parsePriceInput(productData.salePrice),
          isOnSale: Boolean(productData.isOnSale),
        },
      };
    } catch (error) {
      console.error('❌ Create product error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createProduct, loading, error };
};

export default useCreateProduct;
